/**
 * Order Activation — the report Ubi asked for.
 *
 * ⚠ Ported from the VALIDATED mockups, not redesigned: `04-order-activation-admin.html`
 * for Unifiber and `05-order-activation-contractor.html` for a contractor. Same
 * sections in the same order, same classes, same wording. Ubi signed those screens off
 * on 16 Sep 2026, which makes them a contract rather than a suggestion. If something
 * here needs to look different, change the mockup too and say why.
 *
 * Which of the two renders is decided by `meta.internal`, which comes from the SERVER —
 * never from a role read on the client. The client's copy is a convenience for drawing
 * the right chrome; the rows themselves were already scoped before they were sent.
 *
 * Three deliberate omissions from screen 04, following the precedent already set in
 * AppLayout (the mockup's search box and bell are not there yet either): the per-row
 * checkboxes, the "More Filters" button and the mockup-only table-state selector. None
 * of them has anything behind it in chunk 2, and a control that does nothing teaches
 * people the product is broken. They come back with the bulk actions that justify them.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import {
  AGE_BUCKETS,
  connectionStatusTone,
  firstClickDirection,
  formatAddress,
  formatDayMonthYear,
  formatNumber,
  orderTypeLabel,
  orderTypeTone,
  type FacetValue,
  type OrderActivationRow,
  type SortDirection,
  type SortField,
} from "@cocon-stats/shared";
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  FileSpreadsheet,
  GitBranch,
  Inbox,
  Info,
  MapPin,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { CHART_PALETTE, GroupedBars } from "@/components/charts/GroupedBars";
import { DualScrollArea } from "@/components/DualScrollArea";
import { HBars } from "@/components/charts/HBars";
import { ColumnFilter } from "@/components/SortableTable";
import { WeeklyActivation } from "@/components/WeeklyActivation";
import { apiDownload, apiFetch } from "@/lib/api-client";

// ─────────────────────────────────────────────────────────────────────────────
// Wire shapes
// ─────────────────────────────────────────────────────────────────────────────

interface ReportMeta {
  snapshotId: string;
  runDate: string;
  refreshedAt: string;
  /** Null when no daily run is scheduled. */
  nextRunAt: string | null;
  /** Mornings the snapshot is behind the 06:00 schedule; 0 when current. */
  daysBehind: number;
  comparedToRunDate: string | null;
  rowCount: number | null;
  source: string;
  internal: boolean;
  scopeLabel: string;
  /** The newest Deepomatic job update behind the technician names. Null = not connected. */
  deepomaticThrough: string | null;
}

interface Summary {
  total: number;
  jvids: number;
  byContractor: Record<string, number>;
  over90: number;
  over90ByContractor: Record<string, number>;
  oldestExecutedAt: string | null;
  ageBuckets: Array<{ label: string; n: number; tone: string }>;
  weekly: Array<{ key: string; label: string; circet: number; constructel: number; total: number }>;
  orderTypes: Array<{ code: string; label: string; n: number; tone: string }>;
  connectionStatuses: Array<{ code: string; n: number; tone: string }>;
  byPop: {
    shown: Array<{ pop: string; circet: number; constructel: number; unassigned: number; total: number }>;
    /** Every POP in scope — what the filter lists. `shown` is only the charted few. */
    all: Array<{ pop: string; total: number }>;
    totalPops: number;
    mixedPops: number;
    shownOrders: number;
    tailPops: number;
    tailOrders: number;
  };
  rowsInHiddenDrawings: number;
  /** The value lists behind the City and Drawing column filters, A-to-Z. */
  facets: { cities: FacetValue[]; drawings: FacetValue[] };
}

interface SummaryResponse {
  snapshot?: null;
  message?: string;
  meta?: ReportMeta;
  summary?: Summary;
  activated?: { total: number | null; byCompany: Record<string, number> | null; mine: number | null };
}

interface RowsResponse {
  meta?: ReportMeta;
  rows: OrderActivationRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** Exactly how many rows the export holds under these filters — open drawings only. */
  exportCount: number;
}

/** The filters that are a list of values ticked in a dropdown. */
type ListKey = "contractors" | "orderTypes" | "ageBuckets" | "pops" | "connectionStatuses" | "cities" | "projectnames";
/** The filters that are a "contains" box under a column heading. */
type TextKey = "jvid" | "technician" | "orderId" | "utac" | "address";

const LIST_KEYS: readonly ListKey[] = [
  "contractors",
  "orderTypes",
  "ageBuckets",
  "pops",
  "connectionStatuses",
  "cities",
  "projectnames",
];
const TEXT_KEYS: readonly TextKey[] = ["jvid", "technician", "orderId", "utac", "address"];

/**
 * Everything the viewer has asked for, in ONE object.
 *
 * ⚠ The chip bar above the table and the filter row under its headings both write here,
 * and the server applies it once through `applyFilters` (hard rule 9). Two filter states —
 * one per control — would let the chips say one thing while the table did another.
 */
type Filters = Record<ListKey, string[]> &
  Record<TextKey, string> & {
    search: string;
    drawings: "all" | "open" | "hidden";
    /** Inclusive YYYY-MM-DD bounds on the execution date. */
    executedFrom: string;
    executedTo: string;
  };

const EMPTY_TEXT: Record<TextKey, string> = { jvid: "", technician: "", orderId: "", utac: "", address: "" };

const EMPTY_FILTERS: Filters = {
  search: "",
  contractors: [],
  orderTypes: [],
  ageBuckets: [],
  pops: [],
  connectionStatuses: [],
  cities: [],
  projectnames: [],
  drawings: "all",
  executedFrom: "",
  executedTo: "",
  ...EMPTY_TEXT,
};

function buildQuery(filters: Filters, extra: Record<string, string | number> = {}): string {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set("search", filters.search.trim());
  // ⚠ Repeated parameters, never a joined string: a city or a drawing name can contain a
  // comma, and the server would split "a, b" into two values that match nothing.
  for (const key of LIST_KEYS) for (const value of filters[key]) params.append(key, value);
  for (const key of TEXT_KEYS) if (filters[key].trim()) params.set(key, filters[key].trim());
  if (filters.drawings !== "all") params.set("drawings", filters.drawings);
  if (filters.executedFrom) params.set("executedFrom", filters.executedFrom);
  if (filters.executedTo) params.set("executedTo", filters.executedTo);
  for (const [key, value] of Object.entries(extra)) params.set(key, String(value));
  return params.toString();
}

/**
 * `16 Sep 2026, 06:12` for the freshness strip.
 *
 * ⚠ Rendered in Europe/Brussels, and that is NOT a contradiction of the UTC rule. These
 * two values — when the job ran, when it next runs — are real instants, so the reader
 * wants them on their own clock. An order's `executedAt` is the opposite: a naive wall
 * clock from DMP, which is why it goes through `formatDayMonthYear` and stays in UTC.
 * Mixing the two up is how a 22:45 execution drifts to the next day.
 */
function formatMoment(iso: string): string {
  const value = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Brussels",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(value);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  // ⚠ The month name is taken from `formatDayMonthYear`, NOT from Intl's `month: "short"`:
  // en-GB renders September as "Sept", so the freshness strip would read "17 Sept 2026"
  // directly above a table full of "11 Sep 2026". One abbreviation, one place.
  const day = formatDayMonthYear(new Date(`${get("year")}-${get("month")}-${get("day")}T00:00:00Z`));
  return `${day}, ${get("hour")}:${get("minute")}`;
}

/** A `YYYY-MM-DD` run date as "16 Sep 2026". The wire format is not a display format. */
function formatRunDate(runDate: string): string {
  return formatDayMonthYear(new Date(`${runDate}T00:00:00Z`));
}

/**
 * "12 days old", in amber, beside the refresh time — only when a morning run was missed.
 *
 * ⚠ Shown to contractors too. The report's whole point is one number for everybody on
 * one day, and a contractor reading a twelve-day-old backlog as this morning's is exactly
 * the argument it exists to end. The count comes from the server, which knows the
 * schedule; recomputing it here from the browser's clock would be a second rule.
 */
function StaleNote({ meta }: { meta: ReportMeta }) {
  if (meta.daysBehind <= 0) return null;
  return (
    <span className="font-semibold" style={{ color: "#b45309" }}>
      {meta.daysBehind === 1 ? "1 day old" : `${meta.daysBehind} days old`}
    </span>
  );
}

/**
 * The same warning for the technician names, which come from Deepomatic, not DMP.
 *
 * A morning snapshot holds the previous day's file, so names up to two days behind the run
 * date are current. Older means the files stopped being read — an expired key, a moved
 * container — and the names, unlike the backlog, would otherwise give no sign of it.
 */
function TechnicianStaleNote({ meta }: { meta: ReportMeta }) {
  if (!meta.deepomaticThrough) return null;
  const through = meta.deepomaticThrough.slice(0, 10);
  const days = Math.round((Date.parse(`${meta.runDate}T00:00:00Z`) - Date.parse(`${through}T00:00:00Z`)) / 86_400_000);
  if (days <= 2) return null;
  return (
    <span className="font-semibold" style={{ color: "#b45309" }}>
      Technician names from {formatRunDate(through)}
    </span>
  );
}

/** The age pill tone, exactly as `CS.ageTone` in the mockups. */
function ageTone(days: number): string {
  if (days > 365) return "red";
  if (days > 90) return "orange";
  if (days > 30) return "amber";
  return "slate";
}

function Pill({ tone, children, dot, num }: { tone: string; children: React.ReactNode; dot?: boolean; num?: boolean }) {
  return (
    <span className={`pill pill-${tone}${num ? " pill-num" : ""}`}>
      {dot && <span className="pill-dot" style={{ background: "currentColor" }} />}
      {children}
    </span>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className={`chip${active ? " chip-active" : ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The screen
// ─────────────────────────────────────────────────────────────────────────────

export function OrderActivation() {
  const [meta, setMeta] = useState<ReportMeta | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [activated, setActivated] = useState<SummaryResponse["activated"] | null>(null);
  const [noSnapshot, setNoSnapshot] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [searchBox, setSearchBox] = useState("");
  const [textBoxes, setTextBoxes] = useState<Record<TextKey, string>>(EMPTY_TEXT);
  const [sort, setSort] = useState<TableSort>({ field: "jvid", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [rows, setRows] = useState<OrderActivationRow[]>([]);
  const [rowTotal, setRowTotal] = useState(0);
  const [exportCount, setExportCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingRows, setLoadingRows] = useState(true);
  const [exporting, setExporting] = useState(false);

  // The search box is debounced: the table refetches on every keystroke otherwise, and
  // the endpoint scans the whole snapshot each time.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.search === searchBox ? f : { ...f, search: searchBox }));
    }, 250);
    return () => clearTimeout(timer);
  }, [searchBox]);

  // The column "contains" boxes, debounced for the same reason.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (TEXT_KEYS.every((key) => f[key] === textBoxes[key]) ? f : { ...f, ...textBoxes }));
    }, 250);
    return () => clearTimeout(timer);
  }, [textBoxes]);

  const setTextBox = useCallback((key: TextKey, value: string) => {
    setTextBoxes((current) => ({ ...current, [key]: value }));
  }, []);

  const clearAll = useCallback(() => {
    setSearchBox("");
    setTextBoxes(EMPTY_TEXT);
    setFilters(EMPTY_FILTERS);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingSummary(true);
    apiFetch<SummaryResponse>("/reports/order-activation")
      .then((data) => {
        if (cancelled) return;
        if (!data.meta || !data.summary) {
          setNoSnapshot(data.message ?? "No snapshot yet.");
          return;
        }
        setMeta(data.meta);
        setSummary(data.summary);
        setActivated(data.activated ?? null);
      })
      .catch(() => {
        if (!cancelled) setNoSnapshot("The report could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingRows(true);
    const query = buildQuery(filters, { page, pageSize, sort: sort.field, direction: sort.direction });
    apiFetch<RowsResponse>(`/reports/order-activation/rows?${query}`)
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows ?? []);
        setRowTotal(data.total ?? 0);
        setExportCount(data.exportCount ?? 0);
        setTotalPages(data.totalPages ?? 1);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          // Nothing is known about this filter set, so nothing may be exported under it.
          setExportCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRows(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page, pageSize, sort]);

  // Any change to what is being asked for goes back to page 1. Otherwise a filter that
  // leaves three rows while you are on page 40 shows an empty table that looks broken.
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize, sort]);

  const toggle = useCallback((key: ListKey, value: string) => {
    setFilters((f) => {
      const current = f[key];
      return {
        ...f,
        [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  }, []);

  const clearList = useCallback((key: ListKey) => {
    setFilters((f) => ({ ...f, [key]: [] }));
  }, []);

  const activeChips = useMemo(() => {
    const chips: Array<{ label: string; clear: () => void }> = [];
    for (const code of filters.orderTypes) {
      chips.push({ label: `Order type: ${orderTypeLabel(code)}`, clear: () => toggle("orderTypes", code) });
    }
    for (const bucket of filters.ageBuckets) {
      chips.push({ label: `Age: ${bucket}`, clear: () => toggle("ageBuckets", bucket) });
    }
    for (const code of filters.contractors) {
      chips.push({ label: `Contractor: ${code}`, clear: () => toggle("contractors", code) });
    }
    for (const pop of filters.pops) {
      chips.push({ label: `POP: ${pop}`, clear: () => toggle("pops", pop) });
    }
    for (const status of filters.connectionStatuses) {
      chips.push({ label: `Status: ${status}`, clear: () => toggle("connectionStatuses", status) });
    }
    for (const city of filters.cities) {
      chips.push({ label: `City: ${city}`, clear: () => toggle("cities", city) });
    }
    for (const drawing of filters.projectnames) {
      chips.push({ label: `Drawing: ${drawing}`, clear: () => toggle("projectnames", drawing) });
    }
    for (const key of TEXT_KEYS) {
      if (filters[key].trim()) {
        chips.push({
          label: `${TEXT_LABELS[key]} contains: ${filters[key].trim()}`,
          clear: () => {
            setTextBox(key, "");
            setFilters((f) => ({ ...f, [key]: "" }));
          },
        });
      }
    }
    if (filters.drawings !== "all") {
      chips.push({
        label: `Drawings: ${filters.drawings === "open" ? "Open only" : "Hidden only"}`,
        clear: () => setFilters((f) => ({ ...f, drawings: "all" })),
      });
    }
    if (filters.executedFrom || filters.executedTo) {
      chips.push({
        label: `Executed: ${filters.executedFrom || "any"} to ${filters.executedTo || "any"}`,
        clear: () => setFilters((f) => ({ ...f, executedFrom: "", executedTo: "" })),
      });
    }
    if (filters.search.trim()) {
      chips.push({
        label: `Search: ${filters.search.trim()}`,
        clear: () => {
          setSearchBox("");
          setFilters((f) => ({ ...f, search: "" }));
        },
      });
    }
    return chips;
  }, [filters, toggle, setTextBox]);

  const runExport = useCallback(async () => {
    setExporting(true);
    try {
      await apiDownload(
        `/reports/order-activation/export?${buildQuery(filters)}`,
        `order-activation_${meta?.runDate ?? "export"}.xlsx`,
      );
      // The count the server said the file would hold — open drawings only — not the
      // table's, which for Unifiber may include hidden drawings.
      toast.success(`Exported ${formatNumber(exportCount)} rows — semicolon-separated, opens straight into Excel.`);
    } catch {
      toast.error("The export could not be produced.");
    } finally {
      setExporting(false);
    }
  }, [filters, meta?.runDate, exportCount]);

  // Everything the table's two heading rows need, handed down as one object.
  const table: TableControls = {
    sort,
    setSort: (field) =>
      setSort((current) =>
        current.field === field
          ? { field, direction: current.direction === "asc" ? "desc" : "asc" }
          : { field, direction: firstClickDirection(field) },
      ),
    filters,
    setFilters,
    toggle,
    clearList,
    textBoxes,
    setTextBox,
  };

  const exportState: ExportState = {
    count: exportCount,
    rowTotal,
    // ⚠ Not while the rows are loading: the count on the button belongs to the previous
    // filter set until the new page arrives, and the file would not match it.
    busy: exporting || loadingRows,
    exporting,
    run: runExport,
  };

  if (loadingSummary) {
    return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;
  }

  if (noSnapshot || !meta || !summary) {
    return <NoSnapshotYet message={noSnapshot ?? "No snapshot yet."} />;
  }

  return meta.internal ? (
    <AdminView
      meta={meta}
      summary={summary}
      activated={activated}
      filters={filters}
      setFilters={setFilters}
      toggle={toggle}
      activeChips={activeChips}
      clearAll={clearAll}
      searchBox={searchBox}
      setSearchBox={setSearchBox}
      rows={rows}
      rowTotal={rowTotal}
      loadingRows={loadingRows}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      totalPages={totalPages}
      table={table}
      exportState={exportState}
    />
  ) : (
    <ContractorView
      meta={meta}
      summary={summary}
      activated={activated}
      filters={filters}
      setFilters={setFilters}
      toggle={toggle}
      activeChips={activeChips}
      clearAll={clearAll}
      searchBox={searchBox}
      setSearchBox={setSearchBox}
      rows={rows}
      rowTotal={rowTotal}
      loadingRows={loadingRows}
      page={page}
      setPage={setPage}
      totalPages={totalPages}
      table={table}
      exportState={exportState}
    />
  );
}

/**
 * Before the first morning run.
 *
 * ⚠ Says so plainly instead of drawing the dashboard with zeroes in it. A zero on this
 * screen reads as "no backlog", which is the opposite of the truth and exactly the kind
 * of number this product exists to stop people arguing over.
 */
function NoSnapshotYet({ message }: { message: string }) {
  return (
    <div className="px-4 py-6 lg:px-8 lg:py-8">
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
        <Inbox className="mx-auto h-6 w-6 text-slate-300" />
        <h3 className="mt-3 font-semibold text-slate-700">No snapshot yet</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{message}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen 04 — Unifiber
// ─────────────────────────────────────────────────────────────────────────────

interface AdminProps {
  meta: ReportMeta;
  summary: Summary;
  activated: SummaryResponse["activated"] | null;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  toggle: (key: ListKey, value: string) => void;
  activeChips: Array<{ label: string; clear: () => void }>;
  clearAll: () => void;
  searchBox: string;
  setSearchBox: (value: string) => void;
  rows: OrderActivationRow[];
  rowTotal: number;
  loadingRows: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  totalPages: number;
  table: TableControls;
  exportState: ExportState;
}

function AdminView(props: AdminProps) {
  const { meta, summary, activated, filters, setFilters, toggle, activeChips, searchBox, setSearchBox } = props;
  const pop = summary.byPop;
  const topPop = pop.shown[0];

  return (
    <div className="max-w-[1600px] space-y-6 px-4 py-6 lg:px-8">
      {/* Freshness strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="flex items-center gap-2 text-slate-600">
          <RefreshCw className="h-4 w-4 text-slate-400" />
          Refreshed <span className="font-semibold text-slate-800">{formatMoment(meta.refreshedAt)}</span>
          <StaleNote meta={meta} />
          <TechnicianStaleNote meta={meta} />
        </span>
        <span className="hidden text-slate-400 sm:inline">·</span>
        <span className="text-slate-500">{meta.source}</span>
        <span className="hidden text-slate-400 sm:inline">·</span>
        {meta.nextRunAt ? (
          <span className="text-slate-500">
            Next run <span className="font-medium text-slate-700">{formatMoment(meta.nextRunAt)}</span>
          </span>
        ) : (
          <span className="font-medium" style={{ color: "#b45309" }}>
            No daily run scheduled — refreshed by hand
          </span>
        )}
      </div>

      {/* KPI row */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <div className="glass-card card-accent-rose rounded-xl p-5">
          <p className="eyebrow">Awaiting activation</p>
          <p className="num mt-1 text-3xl font-bold text-slate-900">{formatNumber(summary.total)}</p>
          <p className="mt-1 text-xs text-slate-500">{formatNumber(summary.jvids)} addresses</p>
        </div>
        <div className="glass-card card-accent-purple rounded-xl p-5">
          <p className="eyebrow">Circet</p>
          <p className="num mt-1 text-3xl font-bold text-slate-900">{formatNumber(summary.byContractor.circet ?? 0)}</p>
          <p className="mt-1 text-xs" style={{ color: "#c2410c" }}>
            {summary.over90ByContractor.circet ?? 0} over 90 days
          </p>
        </div>
        <div className="glass-card card-accent-orange rounded-xl p-5">
          <p className="eyebrow">Constructel</p>
          <p className="num mt-1 text-3xl font-bold text-slate-900">
            {formatNumber(summary.byContractor.constructel ?? 0)}
          </p>
          <p className="mt-1 text-xs" style={{ color: "#c2410c" }}>
            {summary.over90ByContractor.constructel ?? 0} over 90 days
          </p>
        </div>
        <div className="glass-card card-accent-red rounded-xl p-5">
          <p className="eyebrow">Over 90 days</p>
          <p className="num mt-1 text-3xl font-bold" style={{ color: "#b91c1c" }}>
            {formatNumber(summary.over90)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {summary.oldestExecutedAt ? `Oldest ${formatDayMonthYear(new Date(summary.oldestExecutedAt))}` : "—"}
          </p>
        </div>
        <ActivatedCard activated={activated} meta={meta} />
      </section>

      {/* Charts */}
      <section className="grid gap-4 xl:grid-cols-3">
        <div className="card overflow-hidden p-0 xl:col-span-2">
          <div className="gradient-band px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-white">
            Orders Delivered Per Week
          </div>
          <div className="p-5">
            <GroupedBars
              rows={summary.weekly.map((w) => ({ label: w.label, circet: w.circet, constructel: w.constructel }))}
              series={[
                { key: "circet", label: "Circet", color: CHART_PALETTE.circet! },
                { key: "constructel", label: "Constructel", color: CHART_PALETTE.constructel! },
              ]}
            />
            <p className="mt-3 text-xs text-slate-400">
              Delivery dates come from DMP&rsquo;s <span className="mono">technically_executed_at</span>, counted by ISO
              week over the {formatNumber(summary.total)} orders still awaiting activation.
            </p>
          </div>
        </div>
        <div className="card overflow-hidden p-0">
          <div className="gradient-band px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-white">
            Backlog By Age
          </div>
          <div className="p-5">
            <HBars rows={summary.ageBuckets} />
          </div>
        </div>
      </section>

      {/* Installation orders activated per week — the weekly meetings with each partner run on it */}
      <WeeklyActivation internal />

      {/* Orders per POP */}
      <section className="card overflow-hidden p-0">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-white">
            Orders To Be Activated Per POP
          </span>
          <span className="text-[11px] font-medium text-white/80">
            {pop.totalPops} POPs · top {pop.shown.length} shown
          </span>
        </div>
        <div className="p-5">
          <div className="mb-5 flex flex-wrap items-baseline gap-x-10 gap-y-3">
            <div>
              <p className="eyebrow">Busiest POP</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">
                <span className="num">{formatNumber(topPop?.total ?? 0)}</span>
                <span className="mono ml-1 text-sm font-semibold text-slate-500">{topPop?.pop ?? "—"}</span>
              </p>
            </div>
            <div>
              <p className="eyebrow">Top {pop.shown.length} POPs hold</p>
              <p className="num mt-0.5 text-2xl font-bold text-slate-900">
                {summary.total ? Math.round((pop.shownOrders / summary.total) * 100) : 0}%
              </p>
            </div>
            <div>
              <p className="eyebrow">The other {pop.tailPops} POPs</p>
              <p className="num mt-0.5 text-2xl font-bold text-slate-900">{formatNumber(pop.tailOrders)}</p>
            </div>
          </div>
          <GroupedBars
            width={Math.max(700, pop.shown.length * 84)}
            height={300}
            rotateLabels
            rows={pop.shown.map((p) => ({ label: p.pop, circet: p.circet, constructel: p.constructel }))}
            series={[
              { key: "circet", label: "Circet", color: CHART_PALETTE.circet! },
              { key: "constructel", label: "Constructel", color: CHART_PALETTE.constructel! },
            ]}
          />
          <p className="mt-3 text-xs text-slate-400">
            The {pop.tailPops} POPs not shown hold {formatNumber(pop.tailOrders)} orders between them
            {pop.tailPops > 0 && <> — an average of {Math.round(pop.tailOrders / pop.tailPops)} each</>}.{" "}
            {pop.mixedPops} of the {pop.totalPops} POPs are worked by both contractors.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="card space-y-4 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={searchBox}
              onChange={(e) => setSearchBox(e.target.value)}
              placeholder="Search JVID, technician, order, UTAC, street or city"
              className="field field-icon"
            />
          </div>
          <ExportButton state={props.exportState} className="btn btn-outline" />
          <Link to="/admin/drawings" className="btn gradient-btn">
            <GitBranch className="h-4 w-4" /> Drawing Control
          </Link>
        </div>

        <ExportNote state={props.exportState} internal />

        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Contractor</span>
          <Chip active={filters.contractors.length === 0} onClick={() => setFilters((f) => ({ ...f, contractors: [] }))}>
            All
          </Chip>
          <Chip active={filters.contractors.includes("circet")} onClick={() => toggle("contractors", "circet")}>
            Circet
          </Chip>
          <Chip
            active={filters.contractors.includes("constructel")}
            onClick={() => toggle("contractors", "constructel")}
          >
            Constructel
          </Chip>
          <Chip active={filters.contractors.includes("unassigned")} onClick={() => toggle("contractors", "unassigned")}>
            Unassigned <span className="num">{summary.byContractor.unassigned ?? 0}</span>
          </Chip>

          <span className="mx-2 hidden h-6 w-px bg-slate-200 sm:block" />

          <span className="eyebrow mr-1">Drawings</span>
          <Chip active={filters.drawings === "all"} onClick={() => setFilters((f) => ({ ...f, drawings: "all" }))}>
            All
          </Chip>
          <Chip active={filters.drawings === "open"} onClick={() => setFilters((f) => ({ ...f, drawings: "open" }))}>
            Open only
          </Chip>
          <Chip active={filters.drawings === "hidden"} onClick={() => setFilters((f) => ({ ...f, drawings: "hidden" }))}>
            Hidden only
          </Chip>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Order type</span>
          {summary.orderTypes.map((type) => (
            <Chip
              key={type.code}
              active={filters.orderTypes.includes(type.code)}
              onClick={() => toggle("orderTypes", type.code)}
            >
              {type.label} <span className="num">{formatNumber(type.n)}</span>
            </Chip>
          ))}

          <span className="mx-2 hidden h-6 w-px bg-slate-200 sm:block" />

          <PopFilter summary={summary} filters={filters} toggle={toggle} clear={() => props.table.clearList("pops")} />
          <AgeFilter filters={filters} toggle={toggle} />
          <ExecutedFilter filters={filters} setFilters={setFilters} />
        </div>

        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-1">
            <span className="eyebrow">Active</span>
            {activeChips.map((chip) => (
              <span key={chip.label} className="chip-echo">
                {chip.label}
                <X className="h-3 w-3 cursor-pointer" onClick={chip.clear} />
              </span>
            ))}
            <button type="button" className="ml-1 text-xs font-semibold" style={{ color: "#ed125f" }} onClick={props.clearAll}>
              Clear all
            </button>
          </div>
        )}
      </section>

      {/* Table */}
      <section className="card overflow-hidden">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Orders Awaiting Activation</span>
          <span className="text-xs text-white/85">
            {formatNumber(props.rowTotal)} rows · {formatNumber(summary.rowsInHiddenDrawings)} in hidden drawings
          </span>
        </div>

        <DualScrollArea maxHeight="620px">
          <table className="tbl">
            <OrderTableHead columns={orderColumns(summary, meta, true)} table={props.table} />
            <tbody>
              <TableBody rows={props.rows} loading={props.loadingRows} columns={13} internal />
            </tbody>
          </table>
        </DualScrollArea>

        {!props.loadingRows && props.rows.length > 0 && (
          <Pager
            page={props.page}
            setPage={props.setPage}
            pageSize={props.pageSize}
            setPageSize={props.setPageSize}
            total={props.rowTotal}
            totalPages={props.totalPages}
          />
        )}
      </section>

      <p className="text-xs text-slate-400">
        Source: DMP <span className="mono">internal.&quot;REPORTING_ORDER&quot;</span> joined to{" "}
        <span className="mono">REPORTING_NETWORK</span> and <span className="mono">cocon_customer_connection</span>,
        delivered orders whose COCON connection status is not MTK. Values are shown exactly as DMP stores them.
      </p>
    </div>
  );
}

/**
 * "Activated yesterday".
 *
 * ⚠ Renders an em dash, never a zero, when there is nothing to compare against. "0
 * activated" is an accusation aimed at a named contractor; "—" is the truth. It also
 * names the day it was compared against, because without that nobody can tell one
 * night's work from three days' accumulated over a weekend.
 */
function ActivatedCard({
  activated,
  meta,
}: {
  activated: SummaryResponse["activated"] | null;
  meta: ReportMeta;
}) {
  const total = activated?.total ?? null;
  const byCompany = activated?.byCompany ?? null;
  // The parts must add up to the figure above them, so the unassigned say so when there are any.
  const unassigned = byCompany?.unassigned ?? 0;

  return (
    <div className="glass-card card-accent-emerald col-span-2 rounded-xl p-5 lg:col-span-1">
      <p className="eyebrow">{activatedLabel(meta)}</p>
      <p className="num mt-1 text-3xl font-bold" style={{ color: "#047857" }}>
        {total === null ? "—" : formatNumber(total)}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        {total === null
          ? activationUnknownReason(meta)
          : byCompany
            ? `Installation orders · Circet ${byCompany.circet ?? 0} · Constructel ${byCompany.constructel ?? 0}` +
              (unassigned ? ` · unassigned ${unassigned}` : "")
            : "Installation orders"}
      </p>
    </div>
  );
}

/** Why there is no activation figure: nothing to compare against, or DMP could not be asked. */
function activationUnknownReason(meta: ReportMeta): string {
  return meta.comparedToRunDate ? "DMP could not be checked for MTK at this run" : "Needs a second snapshot";
}

/**
 * "Activated yesterday" — but only when it really was yesterday.
 *
 * ⚠ The mockup says "Activated yesterday" flat, and that is right on almost every
 * morning. It is wrong on a Monday, or after the job has failed once: the diff is
 * against the last SUCCESSFUL snapshot, so it can cover a weekend. Reporting three
 * days' work as one day's would overstate a contractor's overnight output on exactly
 * the number their management is shown. So the common case keeps Ubi's wording and the
 * exception names the day instead.
 */
function activatedLabel(meta: ReportMeta): string {
  if (!meta.comparedToRunDate) return "Activated yesterday";
  const previous = new Date(`${meta.comparedToRunDate}T00:00:00Z`);
  const current = new Date(`${meta.runDate}T00:00:00Z`);
  const days = Math.round((current.getTime() - previous.getTime()) / 86_400_000);
  return days === 1 ? "Activated yesterday" : `Activated since ${formatRunDate(meta.comparedToRunDate)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter panels — each written once, used by the chip bar AND the column headings
// ─────────────────────────────────────────────────────────────────────────────

/** One entry in a value list: what is sent to the server, what is read, how many rows. */
interface ListOption {
  value: string;
  label: string;
  n: number;
}

/**
 * A searchable checkbox list — the POP filter, generalised.
 *
 * ⚠ Written once and used twice: by the POP chip above the table and by every value-list
 * column filter under its headings. Both write the same `Filters` state, so the chip and
 * the heading can never disagree about what is ticked.
 *
 * The search box appears once the list is long enough to need one (the POP list is 74
 * long, the drawing list 133; the status list is 3). Ticked values stay visible even when
 * the search would hide them — otherwise narrowing the search appears to silently drop a
 * filter that is still applied.
 */
function ValueList({
  options,
  selected,
  onToggle,
  onClear,
  noun,
  mono,
}: {
  options: readonly ListOption[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  noun: string;
  mono?: boolean;
}) {
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : [...options];
    const kept = options.filter((o) => selected.includes(o.value) && !matching.includes(o));
    return [...kept, ...matching];
  }, [options, query, selected]);

  return (
    <>
      {options.length > 8 && (
        <div className="relative mb-2">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
            <Search className="h-3.5 w-3.5" />
          </span>
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${noun}`}
            className="field field-sm field-icon-sm"
          />
        </div>
      )}

      <div className="custom-scrollbar max-h-60 overflow-y-auto">
        {shown.length === 0 && <p className="px-2 py-3 text-center text-xs text-slate-400">No {noun} matches.</p>}
        {shown.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="tick"
              checked={selected.includes(option.value)}
              onChange={() => onToggle(option.value)}
            />
            <span className={`flex-1 ${mono ? "mono" : ""}`}>{option.label}</span>
            <span className="num text-slate-400">{formatNumber(option.n)}</span>
          </label>
        ))}
      </div>

      {selected.length > 0 && (
        <button type="button" className="mt-2 px-2 text-xs font-semibold" style={{ color: "#ed125f" }} onClick={onClear}>
          Clear {selected.length} selected
        </button>
      )}
    </>
  );
}

/**
 * The POP chip.
 *
 * ⚠ Lists `byPop.all`, every POP in the viewer's scope — not `byPop.shown`, which is
 * only the fourteen the chart draws. With `shown`, the chip read "POP (74)" above a list
 * of 14 and the other 60 could not be filtered for at all. Busiest first here, because
 * "which POP has the backlog" is the question people bring to this chip; the column
 * filter under the POP heading lists the same POPs A-to-Z, as a spreadsheet would.
 */
function PopFilter({
  summary,
  filters,
  toggle,
  clear,
}: {
  summary: Summary;
  filters: Filters;
  toggle: (key: "pops", value: string) => void;
  clear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => (summary.byPop.all ?? []).map((p) => ({ value: p.pop, label: p.pop, n: p.total })),
    [summary],
  );

  return (
    <span className="relative">
      <Chip active={filters.pops.length > 0} onClick={() => setOpen((o) => !o)}>
        <MapPin className="h-3.5 w-3.5" /> POP{" "}
        <span className="text-slate-400">
          {filters.pops.length > 0 ? `(${filters.pops.length}/${summary.byPop.totalPops})` : `(${summary.byPop.totalPops})`}
        </span>
        <ChevronDown className="h-3.5 w-3.5" />
      </Chip>
      {open && (
        <div className="absolute z-30 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <ValueList
            options={options}
            selected={filters.pops}
            onToggle={(pop) => toggle("pops", pop)}
            onClear={clear}
            noun="POP"
            mono
          />
        </div>
      )}
    </span>
  );
}

function AgeList({ selected, onToggle }: { selected: readonly string[]; onToggle: (bucket: string) => void }) {
  return (
    <>
      {AGE_BUCKETS.map((bucket) => (
        <label key={bucket.label} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50">
          <input
            type="checkbox"
            className="tick"
            checked={selected.includes(bucket.label)}
            onChange={() => onToggle(bucket.label)}
          />
          {bucket.label}
        </label>
      ))}
    </>
  );
}

function AgeFilter({
  filters,
  toggle,
}: {
  filters: Filters;
  toggle: (key: "ageBuckets", value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative">
      <Chip active={filters.ageBuckets.length > 0} onClick={() => setOpen((o) => !o)}>
        <Clock className="h-3.5 w-3.5" /> Age <ChevronDown className="h-3.5 w-3.5" />
      </Chip>
      {open && (
        <div className="absolute z-30 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <AgeList selected={filters.ageBuckets} onToggle={(bucket) => toggle("ageBuckets", bucket)} />
        </div>
      )}
    </span>
  );
}

/**
 * The "Executed" range, screen 04's third dropdown chip.
 *
 * ⚠ Bounds are on the EXECUTION date as DMP recorded it, which is why the inputs are
 * plain dates and the comparison server-side is a string compare on the UTC date part.
 * Anything that routed these through a local-time Date would shift a late-evening
 * execution into the neighbouring day and quietly drop it out of a range that should
 * contain it.
 */
function ExecutedRange({
  filters,
  setFilters,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}) {
  const active = Boolean(filters.executedFrom || filters.executedTo);
  return (
    <div className="p-1">
      <label className="eyebrow mb-1 block">From</label>
      <input
        type="date"
        className="field field-md"
        value={filters.executedFrom}
        onChange={(e) => setFilters((f) => ({ ...f, executedFrom: e.target.value }))}
      />
      <label className="eyebrow mb-1 mt-3 block">To</label>
      <input
        type="date"
        className="field field-md"
        value={filters.executedTo}
        onChange={(e) => setFilters((f) => ({ ...f, executedTo: e.target.value }))}
      />
      {active && (
        <button
          type="button"
          className="mt-3 text-xs font-semibold"
          style={{ color: "#ed125f" }}
          onClick={() => setFilters((f) => ({ ...f, executedFrom: "", executedTo: "" }))}
        >
          Clear dates
        </button>
      )}
    </div>
  );
}

function ExecutedFilter({
  filters,
  setFilters,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}) {
  const [open, setOpen] = useState(false);
  const active = Boolean(filters.executedFrom || filters.executedTo);

  return (
    <span className="relative">
      <Chip active={active} onClick={() => setOpen((o) => !o)}>
        <Calendar className="h-3.5 w-3.5" /> Executed <ChevronDown className="h-3.5 w-3.5" />
      </Chip>
      {open && (
        <div className="absolute z-30 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <ExecutedRange filters={filters} setFilters={setFilters} />
        </div>
      )}
    </span>
  );
}

/**
 * Open or hidden — the Visibility column's filter, and the same `drawings` field the
 * "Drawings" chips set. One choice, not a list: "open" already includes shared drawings,
 * exactly as it does on the chips, so the two controls can never read differently.
 *
 * For a contractor, "open" means open to THEM (`drawingOpen`, set per viewer by
 * `visibleRows`), and "shared" is not theirs to know.
 */
function VisibilityChoice({
  value,
  onChange,
  counts,
  internal,
}: {
  value: Filters["drawings"];
  onChange: (value: Filters["drawings"]) => void;
  counts: { all: number; open: number; hidden: number };
  internal: boolean;
}) {
  const options: Array<{ value: Filters["drawings"]; label: string; n: number }> = [
    { value: "all", label: "All drawings", n: counts.all },
    { value: "open", label: internal ? "Open, including shared" : "Open to you", n: counts.open },
    { value: "hidden", label: "Hidden", n: counts.hidden },
  ];
  return (
    <>
      {options.map((option) => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50">
          <input
            type="radio"
            name="order-activation-visibility"
            className="tick"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="flex-1">{option.label}</span>
          <span className="num text-slate-400">{formatNumber(option.n)}</span>
        </label>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The table's headings: a sort on every column, a filter under every heading
// ─────────────────────────────────────────────────────────────────────────────

interface TableSort {
  field: SortField;
  direction: SortDirection;
}

/** Everything the two heading rows read and write. The same state the chip bar uses. */
interface TableControls {
  sort: TableSort;
  setSort: (field: SortField) => void;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  toggle: (key: ListKey, value: string) => void;
  clearList: (key: ListKey) => void;
  textBoxes: Record<TextKey, string>;
  setTextBox: (key: TextKey, value: string) => void;
}

type HeaderFilterSpec =
  | { kind: "text"; key: TextKey }
  | { kind: "list"; key: ListKey; options: ListOption[]; noun: string; mono?: boolean }
  | { kind: "age" }
  | { kind: "executed" }
  | { kind: "visibility"; counts: { all: number; open: number; hidden: number }; internal: boolean };

interface OrderColumn {
  label: string;
  sort: SortField;
  filter: HeaderFilterSpec;
  /** Shown on hover over the heading, above "Click to sort". */
  note?: string;
}

const TEXT_LABELS: Record<TextKey, string> = {
  jvid: "JVID",
  technician: "Technician",
  orderId: "Order",
  utac: "UTAC",
  address: "Address",
};

/**
 * What the Technician heading says on hover: where the name comes from, why a row can have
 * none, and how fresh the names are. A column that is empty on a third of the rows has to
 * say why on itself (hard rule 30), or the empty cells read as a fault.
 */
function technicianNote(meta: ReportMeta, internal: boolean): string {
  if (!meta.deepomaticThrough) return "Technician from Deepomatic field photos — not connected yet.";
  const parts = [
    "Whoever did the latest facade, pole, trenching or MDU job at this address, from Deepomatic's field photos.",
    "— when Deepomatic has no such job there.",
    `Deepomatic data up to ${formatMoment(meta.deepomaticThrough)}.`,
  ];
  if (!internal) parts.splice(2, 0, "Names are shown on your own orders only.");
  return parts.join("\n");
}

function contractorOptions(summary: Summary): ListOption[] {
  const named = Object.entries(summary.byContractor)
    .filter(([code]) => code !== "unassigned")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, n]) => ({ value: code, label: capitalise(code), n }));
  const unassigned = summary.byContractor.unassigned ?? 0;
  // Last, like every missing value in this product's tables.
  return unassigned > 0 ? [...named, { value: "unassigned", label: "Unassigned", n: unassigned }] : named;
}

/**
 * The columns of each table, in the order they render, with what each sorts and filters on.
 *
 * Ubi, Cocon stats reporting feedback (Sep 2026): *"Orders Awaiting Activation tile - all
 * columns need sort and filter, visible to all"*. So there is no column without both, and
 * the contractor's table is built by the same function as Unifiber's — it differs only in
 * the column a contractor does not have (Contractor) and the one they do (UTAC). Since
 * 13 Oct 2026 both have Visibility: a contractor sees their own orders in hidden drawings.
 *
 * ⚠ Every value list is counted over the viewer's SCOPED rows (the summary), so a
 * contractor's City list names only the cities in their own work.
 */
function orderColumns(summary: Summary, meta: ReportMeta, internal: boolean): OrderColumn[] {
  const alphabetical = (options: ListOption[]) => [...options].sort((a, b) => a.label.localeCompare(b.label));
  const columns: OrderColumn[] = [
    { label: "JVID", sort: "jvid", filter: { kind: "text", key: "jvid" } },
    // Ubi, 13 Oct 2026: "the technician name next to the JVID".
    {
      label: "Technician",
      sort: "technician",
      filter: { kind: "text", key: "technician" },
      note: technicianNote(meta, internal),
    },
    { label: "Order", sort: "order", filter: { kind: "text", key: "orderId" } },
    {
      label: "Type",
      sort: "type",
      filter: {
        kind: "list",
        key: "orderTypes",
        noun: "type",
        options: summary.orderTypes.map((t) => ({ value: t.code, label: t.label, n: t.n })),
      },
    },
  ];
  if (internal) {
    columns.push({
      label: "Contractor",
      sort: "contractor",
      filter: { kind: "list", key: "contractors", noun: "contractor", options: contractorOptions(summary) },
    });
  }
  columns.push(
    { label: "Address", sort: "address", filter: { kind: "text", key: "address" } },
    {
      label: "City",
      sort: "city",
      filter: {
        kind: "list",
        key: "cities",
        noun: "city",
        options: summary.facets.cities.map((f) => ({ value: f.value, label: f.value, n: f.n })),
      },
    },
    {
      label: "POP",
      sort: "pop",
      filter: {
        kind: "list",
        key: "pops",
        noun: "POP",
        mono: true,
        options: alphabetical(summary.byPop.all.map((p) => ({ value: p.pop, label: p.pop, n: p.total }))),
      },
    },
    {
      label: "Drawing",
      sort: "drawing",
      filter: {
        kind: "list",
        key: "projectnames",
        noun: "drawing",
        mono: true,
        options: summary.facets.drawings.map((f) => ({ value: f.value, label: f.value, n: f.n })),
      },
    },
    {
      label: "Status",
      sort: "status",
      filter: {
        kind: "list",
        key: "connectionStatuses",
        noun: "status",
        options: summary.connectionStatuses.map((s) => ({ value: s.code, label: s.code, n: s.n })),
      },
    },
  );
  if (!internal) columns.push({ label: "UTAC", sort: "utac", filter: { kind: "text", key: "utac" } });
  columns.push(
    { label: "Executed", sort: "executed", filter: { kind: "executed" } },
    { label: "Age", sort: "age", filter: { kind: "age" } },
  );
  columns.push({
    label: "Visibility",
    sort: "visibility",
    filter: {
      kind: "visibility",
      internal,
      counts: {
        all: summary.total,
        open: summary.total - summary.rowsInHiddenDrawings,
        hidden: summary.rowsInHiddenDrawings,
      },
    },
    note: internal
      ? undefined
      : "Open: Unifiber has opened this drawing to you.\nHidden: not opened to you yet. The order is yours in DMP, shown so your backlog is complete.",
  });
  return columns;
}

/**
 * The two heading rows: a sort button on every heading, a filter under every heading.
 *
 * ⚠ The filters sit in a ROW under the headings, always visible — the pattern Hugues asked
 * for on 17 Sep 2026 and `SortableTable` already uses (*"include the search functionality
 * on the column headers/labels themselves"*), rather than behind a funnel icon. The "contains"
 * columns are typed into in place; the value-list columns open their list from that row.
 *
 * ⚠ Unlike `SortableTable`, nothing here filters or sorts in the browser. The table is
 * paged by the server, so every heading writes to the shared `Filters`/sort state and the
 * API does the work over the WHOLE row set — sorting one page of twenty in the browser
 * would put the wrong rows on page one.
 */
function OrderTableHead({ columns, table }: { columns: OrderColumn[]; table: TableControls }) {
  return (
    <thead className="tbl-head-sticky">
      <tr>
        {columns.map((column) => {
          const active = table.sort.field === column.sort;
          return (
            <th
              key={column.sort}
              aria-sort={active ? (table.sort.direction === "asc" ? "ascending" : "descending") : "none"}
            >
              <button
                type="button"
                onClick={() => table.setSort(column.sort)}
                title={column.note ? `${column.note}\n\nClick to sort` : "Click to sort"}
                className="sort-header inline-flex items-center gap-1"
              >
                {column.label}
                {active ? (
                  table.sort.direction === "asc" ? (
                    <ChevronUp className="h-3 w-3" style={{ color: "#ed125f" }} />
                  ) : (
                    <ChevronDown className="h-3 w-3" style={{ color: "#ed125f" }} />
                  )
                ) : (
                  <ChevronsUpDown className="h-3 w-3 text-slate-300" />
                )}
              </button>
            </th>
          );
        })}
      </tr>
      <tr className="tbl-filter-row">
        {columns.map((column) => (
          <th key={column.sort}>
            <HeaderFilter spec={column.filter} label={column.label} table={table} />
          </th>
        ))}
      </tr>
    </thead>
  );
}

function HeaderFilter({ spec, label, table }: { spec: HeaderFilterSpec; label: string; table: TableControls }) {
  const { filters } = table;

  switch (spec.kind) {
    case "text":
      return (
        <div className="min-w-[110px]">
          <ColumnFilter label={label} value={table.textBoxes[spec.key]} onChange={(next) => table.setTextBox(spec.key, next)} />
        </div>
      );

    case "list": {
      const selected = filters[spec.key];
      const only = selected.length === 1 ? spec.options.find((o) => o.value === selected[0])?.label ?? selected[0] : null;
      return (
        <HeaderPopover
          label={label}
          summary={selected.length === 0 ? "All" : only ?? `${selected.length} selected`}
          active={selected.length > 0}
        >
          <ValueList
            options={spec.options}
            selected={selected}
            onToggle={(value) => table.toggle(spec.key, value)}
            onClear={() => table.clearList(spec.key)}
            noun={spec.noun}
            mono={spec.mono}
          />
        </HeaderPopover>
      );
    }

    case "age": {
      const selected = filters.ageBuckets;
      return (
        <HeaderPopover
          label={label}
          width={190}
          summary={selected.length === 0 ? "All" : selected.length === 1 ? selected[0]! : `${selected.length} selected`}
          active={selected.length > 0}
        >
          <AgeList selected={selected} onToggle={(bucket) => table.toggle("ageBuckets", bucket)} />
        </HeaderPopover>
      );
    }

    case "executed": {
      const { executedFrom: from, executedTo: to } = filters;
      const summary =
        from && to
          ? `${formatRunDate(from)} – ${formatRunDate(to)}`
          : from
            ? `From ${formatRunDate(from)}`
            : to
              ? `Until ${formatRunDate(to)}`
              : "Any date";
      return (
        <HeaderPopover label={label} width={230} summary={summary} active={Boolean(from || to)}>
          <ExecutedRange filters={filters} setFilters={table.setFilters} />
        </HeaderPopover>
      );
    }

    case "visibility":
      return (
        <HeaderPopover
          label={label}
          width={230}
          summary={filters.drawings === "all" ? "All" : filters.drawings === "open" ? "Open" : "Hidden"}
          active={filters.drawings !== "all"}
        >
          <VisibilityChoice
            value={filters.drawings}
            onChange={(drawings) => table.setFilters((f) => ({ ...f, drawings }))}
            counts={spec.counts}
            internal={spec.internal}
          />
        </HeaderPopover>
      );
  }
}

/**
 * A column filter's list, opened from the filter row.
 *
 * ⚠ Rendered into `document.body` with fixed positioning, deliberately. The table sits in a
 * scrolling pane (`DualScrollArea`, overflow auto), and a dropdown positioned inside it is
 * clipped at the pane's edge — a list of 133 drawings opened from a heading would show its
 * first four. That clipping is why `SortableTable` refused popovers altogether; a portal
 * removes the reason rather than the list. It follows its heading when the page or the
 * pane scrolls, and closes on Escape or a click anywhere else.
 */
function HeaderPopover({
  label,
  summary,
  active,
  width = 256,
  children,
}: {
  label: string;
  summary: string;
  active: boolean;
  width?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = window.innerHeight - rect.bottom;
    // Upwards only when there is clearly more room above: a list with a search box needs
    // about 330px, and a panel that runs off the bottom of the window cannot be reached.
    setPosition(
      below < 340 && rect.top > below
        ? { position: "fixed", left, bottom: window.innerHeight - rect.top + 4, width }
        : { position: "fixed", left, top: rect.bottom + 4, width },
    );
  }, [width]);

  useEffect(() => {
    if (!open) return;
    place();
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onScroll = (event: Event) => {
      // Scrolling the list inside the panel is not a reason to move the panel.
      if (panelRef.current?.contains(event.target as Node)) return;
      place();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="field field-sm field-filter flex min-w-[96px] items-center justify-between gap-1 text-left"
        style={active ? { borderColor: "rgba(237, 18, 95, .4)", color: "#ed125f", fontWeight: 600 } : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Filter by ${label}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={`Filter by ${label}`}
            className="z-50 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-lg"
            style={position}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The export — open drawings only, and the count said before the click
// ─────────────────────────────────────────────────────────────────────────────

interface ExportState {
  /** Rows the file will hold under the current filters: open drawings only. From the server. */
  count: number;
  /** Rows the table holds under the same filters. */
  rowTotal: number;
  busy: boolean;
  exporting: boolean;
  run: () => void;
}

/**
 * The export button, carrying the exact number of rows the file will hold.
 *
 * ⚠ Hard rule 24: the figure is the server's `exportCount` — `exportRows(selectRows(…))`,
 * the very rows the export endpoint will write — never the table's count and never "up to".
 * For Unifiber the two differ whenever hidden drawings are on screen, and `ExportNote` says
 * by how much. Disabled at zero, with the reason in the note rather than only in a tooltip.
 */
function ExportButton({ state, className }: { state: ExportState; className: string }) {
  return (
    <button type="button" className={className} onClick={state.run} disabled={state.busy || state.count === 0}>
      <FileSpreadsheet className="h-4 w-4" />
      {state.exporting
        ? "Preparing…"
        : `Export ${formatNumber(state.count)} ${state.count === 1 ? "row" : "rows"} to Excel`}
    </button>
  );
}

/**
 * What the export will and will not contain, stated where the button is.
 *
 * Ubi: *"Export to excel must contain open drawings only"* — Unifiber's file. There the rule
 * can make the file shorter than the table, and a file silently shorter than the screen is
 * exactly the argument this product exists to end.
 *
 * A contractor's file holds every row their table does, hidden ones included (13 Oct 2026),
 * so the note says that those are in it and how they are marked.
 */
function ExportNote({
  state,
  internal,
  includesHidden = false,
}: {
  state: ExportState;
  internal: boolean;
  /** A contractor's file under these filters may hold rows in drawings not open to them. */
  includesHidden?: boolean;
}) {
  const leftOut = state.rowTotal - state.count;

  if (internal && leftOut > 0) {
    return (
      <p className="flex items-start gap-1.5 text-xs" style={{ color: "#b45309" }}>
        <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
        {state.count === 0 ? (
          <span>
            Nothing to export: all <span className="num font-semibold">{formatNumber(state.rowTotal)}</span> rows shown
            are in hidden drawings, and the Excel file only ever holds open drawings.
          </span>
        ) : (
          <span>
            The Excel file holds the <span className="num font-semibold">{formatNumber(state.count)}</span> rows in open
            drawings. The other <span className="num font-semibold">{formatNumber(leftOut)}</span> shown here are in hidden
            drawings and stay out of it.
          </span>
        )}
      </p>
    );
  }

  if (state.rowTotal === 0) {
    return <p className="text-xs text-slate-400">No rows match these filters, so there is nothing to export.</p>;
  }

  return (
    <p className="flex items-center gap-1.5 text-xs text-slate-400">
      <FileSpreadsheet className="h-3.5 w-3.5" />
      {internal
        ? "The export holds every row matching the filters, not just this page — open drawings only, always."
        : includesHidden
          ? "The export contains every row matching the filters, not just this page. Orders in hidden drawings are included and marked in its Drawing state column — choose Open only to leave them out."
          : "The export contains every row matching the filters, not just this page — hand it straight to your teams."}
    </p>
  );
}

function TableBody({
  rows,
  loading,
  columns,
  internal,
}: {
  rows: OrderActivationRow[];
  loading: boolean;
  columns: number;
  internal: boolean;
}) {
  if (loading) {
    return (
      <>
        {Array.from({ length: 8 }, (_, i) => (
          <tr key={i}>
            {Array.from({ length: columns }, (_, c) => (
              <td key={c}>
                <div className="skeleton h-3.5 w-full" />
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={columns} className="py-16 text-center">
          <Inbox className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No orders match these filters.</p>
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <tr key={row.rowKey}>
          <td>
            <span className="mono font-bold" style={{ color: "#ed125f" }}>
              {row.jvid}
            </span>
          </td>
          <td className="text-slate-700">
            {row.technician ?? <span className="text-slate-400">—</span>}
          </td>
          <td className="mono text-slate-500">{row.orderId}</td>
          <td>
            <Pill tone={orderTypeTone(row.orderType)}>{orderTypeLabel(row.orderType)}</Pill>
          </td>
          {internal && (
            <td className="text-slate-700">
              {row.contractor ? (
                capitalise(row.contractor)
              ) : (
                // Em dash for an empty value, never "N/A" — and never blank, because a
                // blank cell reads as a rendering fault rather than as missing data.
                <span className="text-slate-400">—</span>
              )}
            </td>
          )}
          <td className="text-slate-700">{formatAddress(row)}</td>
          <td className="text-slate-600">{row.city}</td>
          <td className="mono text-slate-600">{row.areaPop}</td>
          <td className="mono text-slate-700">{row.projectname}</td>
          <td>
            <Pill tone={connectionStatusTone(row.connectionStatus)} dot>
              {row.connectionStatus}
            </Pill>
          </td>
          {!internal && <td className="mono text-slate-500">{row.utac}</td>}
          <td className="whitespace-nowrap text-slate-600">{formatDayMonthYear(new Date(row.executedAt))}</td>
          <td>
            <Pill tone={ageTone(row.ageDays)} num>
              {row.ageDays} {row.ageDays === 1 ? "day" : "days"}
            </Pill>
          </td>
          <td>
            {/* "Shared": open to more than one contractor, each seeing only their own
                orders in it. Visible at a glance from the report, not only from Drawing
                Control. A contractor gets Open or Hidden only — open to THEM — and never
                who else has the drawing. */}
            {!row.drawingOpen ? (
              <Pill tone="slate">Hidden</Pill>
            ) : !internal || row.drawingAudience.length === 1 ? (
              <Pill tone="emerald">Open</Pill>
            ) : (
              <Pill tone="violet">Shared</Pill>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Pager({
  page,
  setPage,
  pageSize,
  setPageSize,
  total,
  totalPages,
}: {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize?: (size: number) => void;
  total: number;
  totalPages: number;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 text-sm">
      <span className="text-slate-500">
        Showing{" "}
        <span className="font-semibold text-slate-700">
          {from}–{to}
        </span>{" "}
        of <span className="num font-semibold text-slate-700">{formatNumber(total)}</span>
      </span>
      <div className="flex items-center gap-2">
        {setPageSize && (
          <select
            className="h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-600"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        )}
        <button type="button" className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </button>
        <span className="px-1 text-xs text-slate-500">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen 05 — a contractor
// ─────────────────────────────────────────────────────────────────────────────

interface ContractorProps {
  meta: ReportMeta;
  summary: Summary;
  activated: SummaryResponse["activated"] | null;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  toggle: (key: ListKey, value: string) => void;
  activeChips: Array<{ label: string; clear: () => void }>;
  clearAll: () => void;
  searchBox: string;
  setSearchBox: (value: string) => void;
  rows: OrderActivationRow[];
  rowTotal: number;
  loadingRows: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  table: TableControls;
  exportState: ExportState;
}

function ContractorView(props: ContractorProps) {
  const { meta, summary, activated, filters, toggle, activeChips, searchBox, setSearchBox } = props;
  const pop = summary.byPop;
  const topPop = pop.shown[0];
  const activatedMine = activated?.mine ?? null;
  const hidden = summary.rowsInHiddenDrawings;

  return (
    <div className="max-w-[1500px] space-y-6 px-4 py-6 lg:px-8">
      {/* Scope + read-only notice */}
      <div className="card flex flex-wrap items-center gap-x-4 gap-y-2 p-4" style={{ borderLeft: "4px solid #3b82f6" }}>
        <Info className="h-5 w-5 shrink-0" style={{ color: "#1d4fd7" }} />
        <p className="min-w-[260px] flex-1 text-sm text-slate-700">
          <span className="font-semibold">{meta.scopeLabel}</span> orders only, read-only.{" "}
          {meta.nextRunAt ? "Refreshed every morning" : "Refreshed"} from Unifiber&rsquo;s network database.
        </p>
        <span className="flex items-center gap-2 text-sm text-slate-500">
          <RefreshCw className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-700">{formatMoment(meta.refreshedAt)}</span>
          <StaleNote meta={meta} />
          <TechnicianStaleNote meta={meta} />
        </span>
      </div>

      {/* KPI row */}
      <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <div className="glass-card card-accent-rose rounded-xl p-5">
          <p className="eyebrow">To activate</p>
          <p className="num mt-1 text-3xl font-bold text-slate-900">{formatNumber(summary.total)}</p>
          {/* ⚠ Split whenever hidden drawings are in the figure: an order in a drawing not
              open to them is theirs, but not yet theirs to act on, and the headline must not
              read as if every one of them were. */}
          <p className="mt-1 text-xs text-slate-500">
            {hidden > 0 ? (
              <>
                <span className="num">{formatNumber(summary.total - hidden)}</span> open to you ·{" "}
                <span className="num">{formatNumber(hidden)}</span> hidden
              </>
            ) : (
              "In drawings open to you"
            )}
          </p>
        </div>
        <div className="glass-card card-accent-red rounded-xl p-5">
          <p className="eyebrow">Over 90 days</p>
          <p className="num mt-1 text-3xl font-bold" style={{ color: "#b91c1c" }}>
            {formatNumber(summary.over90)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {summary.total ? `${Math.round((summary.over90 / summary.total) * 100)}% of your backlog` : "—"}
          </p>
        </div>
        <div className="glass-card card-accent-emerald rounded-xl p-5">
          <p className="eyebrow">Activated by you</p>
          <p className="num mt-1 text-3xl font-bold" style={{ color: "#047857" }}>
            {activatedMine === null ? "—" : formatNumber(activatedMine)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {activatedMine === null
              ? activationUnknownReason(meta)
              : meta.comparedToRunDate
                ? `Installation orders since ${formatRunDate(meta.comparedToRunDate)}`
                : "Installation orders since the last snapshot"}
          </p>
        </div>
        <div className="glass-card card-accent-orange rounded-xl p-5">
          <p className="eyebrow">Oldest outstanding</p>
          <p className="num mt-1 text-3xl font-bold text-slate-900">
            {summary.oldestExecutedAt ? formatDayMonthYear(new Date(summary.oldestExecutedAt)) : "—"}
          </p>
          <p className="mt-1 text-xs text-slate-500">Delivered, still awaiting activation</p>
        </div>
      </section>

      {/* Charts */}
      <section className="grid gap-4 xl:grid-cols-3">
        <div className="card overflow-hidden p-0 xl:col-span-2">
          <div className="gradient-band px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-white">
            Delivered To You Per Week
          </div>
          <div className="p-5">
            <GroupedBars
              rows={summary.weekly.map((w) => ({ label: w.label, delivered: w.total }))}
              series={[{ key: "delivered", label: "Delivered to you", color: CHART_PALETTE.circet! }]}
            />
            {/* ⚠ The mockup pairs this with an "Activated by you" series. That series
                needs two consecutive daily snapshots to exist at all, so it is left out
                until there are some rather than drawn as a row of zeroes — which would
                read as "you activated nothing", the exact accusation this report must
                not make by accident. */}
            <p className="mt-3 text-xs text-slate-400">
              Orders delivered to you, by the week DMP recorded them. The matching
              &ldquo;activated by you&rdquo; series appears once two consecutive daily snapshots exist.
            </p>
          </div>
        </div>
        <div className="card overflow-hidden p-0">
          <div className="gradient-band px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-white">
            Your Backlog By Age
          </div>
          <div className="p-5">
            <HBars rows={summary.ageBuckets} />
          </div>
        </div>
      </section>

      {/* Your installation orders activated per week — scoped by the server to your own */}
      <WeeklyActivation internal={false} />

      {/* Per POP */}
      <section className="card overflow-hidden p-0">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-white">
            Your Orders To Be Activated Per POP
          </span>
          <span className="text-[11px] font-medium text-white/80">
            {pop.totalPops} POPs · top {pop.shown.length} shown
          </span>
        </div>
        <div className="p-5">
          <div className="mb-5 flex flex-wrap items-baseline gap-x-10 gap-y-3">
            <div>
              <p className="eyebrow">Your busiest POP</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">
                <span className="num">{formatNumber(topPop?.total ?? 0)}</span>
                <span className="mono ml-1 text-sm font-semibold text-slate-500">{topPop?.pop ?? "—"}</span>
              </p>
            </div>
            <div>
              <p className="eyebrow">Your other POPs</p>
              <p className="num mt-0.5 text-2xl font-bold text-slate-900">{formatNumber(pop.tailOrders)}</p>
            </div>
          </div>
          {/* ⚠ One series, and it counts THIS company's orders within each POP — never
              the POP's whole backlog. 12 of the 74 POPs are worked by both contractors,
              so a POP total would tell a Circet user how much Constructel is carrying. */}
          <GroupedBars
            width={Math.max(700, pop.shown.length * 84)}
            height={300}
            rotateLabels
            rows={pop.shown.map((p) => ({ label: p.pop, mine: p.total }))}
            series={[{ key: "mine", label: "Your orders", color: CHART_PALETTE.circet! }]}
          />
          <p className="mt-3 text-xs text-slate-400">
            Your {pop.totalPops} POPs hold {formatNumber(summary.total)} orders awaiting activation.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="card space-y-4 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={searchBox}
              onChange={(e) => setSearchBox(e.target.value)}
              placeholder="Search JVID, technician, order, UTAC, street or city"
              className="field field-icon"
            />
          </div>
          <ExportButton state={props.exportState} className="btn gradient-btn" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Order type</span>
          {summary.orderTypes.map((type) => (
            <Chip
              key={type.code}
              active={filters.orderTypes.includes(type.code)}
              onClick={() => toggle("orderTypes", type.code)}
            >
              {type.label} <span className="num">{formatNumber(type.n)}</span>
            </Chip>
          ))}

          <span className="mx-2 hidden h-6 w-px bg-slate-200 sm:block" />

          <AgeFilter filters={filters} toggle={toggle} />
          <ExecutedFilter filters={filters} setFilters={props.setFilters} />
        </div>

        {/* The same three chips as Unifiber's screen, writing the same `drawings` field as
            the Visibility column's filter — "open" meaning open to them. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Drawings</span>
          <Chip active={filters.drawings === "all"} onClick={() => props.setFilters((f) => ({ ...f, drawings: "all" }))}>
            All <span className="num">{formatNumber(summary.total)}</span>
          </Chip>
          <Chip active={filters.drawings === "open"} onClick={() => props.setFilters((f) => ({ ...f, drawings: "open" }))}>
            Open only <span className="num">{formatNumber(summary.total - hidden)}</span>
          </Chip>
          <Chip
            active={filters.drawings === "hidden"}
            onClick={() => props.setFilters((f) => ({ ...f, drawings: "hidden" }))}
          >
            Hidden only <span className="num">{formatNumber(hidden)}</span>
          </Chip>
        </div>

        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-1">
            <span className="eyebrow">Active</span>
            {activeChips.map((chip) => (
              <span key={chip.label} className="chip-echo">
                {chip.label}
                <X className="h-3 w-3 cursor-pointer" onClick={chip.clear} />
              </span>
            ))}
            <button type="button" className="ml-1 text-xs font-semibold" style={{ color: "#ed125f" }} onClick={props.clearAll}>
              Clear all
            </button>
          </div>
        )}

        <ExportNote state={props.exportState} internal={false} includesHidden={hidden > 0 && filters.drawings !== "open"} />
      </section>

      {/* Table */}
      <section className="card overflow-hidden">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Orders To Activate</span>
          <span className="text-xs text-white/85">
            {formatNumber(props.rowTotal)} rows
            {hidden > 0 && <> · {formatNumber(hidden)} in hidden drawings</>}
          </span>
        </div>

        <DualScrollArea maxHeight="560px">
          <table className="tbl">
            <OrderTableHead columns={orderColumns(summary, meta, false)} table={props.table} />
            <tbody>
              <TableBody rows={props.rows} loading={props.loadingRows} columns={13} internal={false} />
            </tbody>
          </table>
        </DualScrollArea>

        {!props.loadingRows && props.rows.length > 0 && (
          <Pager
            page={props.page}
            setPage={props.setPage}
            pageSize={20}
            total={props.rowTotal}
            totalPages={props.totalPages}
          />
        )}
      </section>

      <p className="text-xs text-slate-400">
        Your orders in drawings Unifiber has not opened to you yet are shown too, marked Hidden. If a project you expect
        is missing, contact your Unifiber contact.
      </p>
    </div>
  );
}
